import type {
  AcquisitionChannel as GeneratedAcquisitionChannel,
  FailureCategory,
  FailureReason,
  SubscriptionAvailableAction as GeneratedSubscriptionAvailableAction,
  SubscriptionDataTableFailure,
  SubscriptionDataTablePersonDeclaringFailure,
  SubscriptionDataTableResponse,
  SubscriptionPipelineFailures,
  SubscriptionPipelineTag as GeneratedSubscriptionPipelineTag,
  SubscriptionView,
  SubscriptionViewStatuses,
} from "@/.generated/hey/subscriber-backend/types.gen";
import type {
  FailureCategoryModel,
  FailureReasonModel,
  SubscriptionAvailableAction,
  SubscriptionDataTableResult,
  SubscriptionDataTableRow,
  SubscriptionDelayedClosureRequest,
  SubscriptionFailure,
  SubscriptionHistoryFailure,
  SubscriptionHistoryFailureAuthor,
  SubscriptionModel,
  SubscriptionOnHoldPeriod,
  SubscriptionPartner,
  SubscriptionPipelineFailuresModel,
  SubscriptionPipelineTag,
  SubscriptionStatuses,
  SubscriptionSubscriber,
  SubscriptionTag,
} from "@/domains/subscription/subscription.types";
import type {
  LegalEntityId,
  SiteId,
  SubscriberId,
  SubscriptionFailureCategoryId,
  SubscriptionFailureReasonId,
  SubscriptionId,
  SubscriptionPipelineId,
  SubscriptionTagId,
} from "@/types/common/ids";

import { enumValueOrRaw } from "@voltalis/frontend-utils";

import {
  AcquisitionChannel,
  ActionScope,
  DelayedClosureProcessingResult,
  EligibilityConfirmationStatus,
  InstallationStatus,
  ProspectionStatus,
  sortSubscriptionTags,
  StatusTransitionAction,
  SubscriptionStatus,
} from "@/domains/subscription/subscription.types";
import { brandNumber, brandString } from "@/lib/branded";
import { StringDate } from "@/lib/string-date";

export function mapSubscriptionView(raw: SubscriptionView): SubscriptionModel {
  return {
    id: brandNumber<SubscriptionId>(raw.id),
    pipelineId: brandNumber<SubscriptionPipelineId>(raw.pipelineId),
    statuses: mapSubscriptionStatuses(raw.statuses),
    subscriber: mapSubscriber(raw.subscriber),
    siteId: brandNumber<SiteId>(raw.siteId),
    partner: mapPartner(raw.partner),
    acquisitionChannel: mapAcquisitionChannel(raw.acquisitionChannel),
    creationDatetime: StringDate.fromIsoLocalDateTime(raw.creationDatetime),
    closureDatetime: raw.closureDatetime ? StringDate.fromIsoLocalDateTime(raw.closureDatetime) : undefined,
    tags: sortSubscriptionTags(raw.tags.map(mapTag)),
    onHoldPeriods: raw.onHoldPeriods.map(mapOnHoldPeriod),
    delayedClosureRequests: raw.delayedClosureRequests.map(mapDelayedClosureRequest),
    failure: raw.failure ? mapFailure(raw.failure) : undefined,
    actions: raw.actions.map(mapAction),
    subscriptionClosedAsConflict:
      raw.subscriptionClosedAsConflict !== null
        ? brandNumber<SubscriptionId>(raw.subscriptionClosedAsConflict)
        : undefined,
  };
}

export function mapAcquisitionChannel(raw: GeneratedAcquisitionChannel): AcquisitionChannel {
  return enumValueOrRaw(AcquisitionChannel, raw);
}

function mapAction(raw: GeneratedSubscriptionAvailableAction): SubscriptionAvailableAction {
  return {
    action: StatusTransitionAction[raw.action],
    available: raw.available,
    scopes: raw.scopes.map((scope) => enumValueOrRaw(ActionScope, scope)),
  };
}

export function mapSubscriptionStatuses(raw: SubscriptionViewStatuses): SubscriptionStatuses {
  return {
    status: enumValueOrRaw(SubscriptionStatus, raw.status),
    installationStatus: enumValueOrRaw(InstallationStatus, raw.installationStatus),
    prospectionStatus: enumValueOrRaw(ProspectionStatus, raw.prospectionStatus),
    eligibilityConfirmationStatus: enumValueOrRaw(EligibilityConfirmationStatus, raw.eligibilityConfirmationStatus),
  };
}

function mapSubscriber(raw: SubscriptionView["subscriber"]): SubscriptionSubscriber {
  return {
    id: brandNumber<SubscriberId>(raw.id),
    firstName: raw.firstName,
    lastName: raw.lastName,
  };
}

function mapPartner(raw: SubscriptionView["partner"]): SubscriptionPartner {
  return {
    id: brandNumber<LegalEntityId>(raw.id),
    note: raw.note,
  };
}

function mapTag(raw: SubscriptionView["tags"][number]): SubscriptionTag {
  return {
    id: brandNumber<SubscriptionTagId>(raw.id),
    value: raw.value,
    path: raw.path,
  };
}

export function mapSubscriptionPipelineTags(raw: GeneratedSubscriptionPipelineTag[]): SubscriptionPipelineTag[] {
  return raw.map((tag) => ({
    id: brandNumber(tag.id),
    parentId: tag.parentId !== null ? brandNumber(tag.parentId) : undefined,
    value: tag.value,
    path: tag.path,
    children: mapSubscriptionPipelineTags(tag.children ?? []),
  }));
}

function mapOnHoldPeriod(raw: SubscriptionView["onHoldPeriods"][number]): SubscriptionOnHoldPeriod {
  return {
    id: brandNumber(raw.id),
    start: StringDate.fromIsoLocalDateTime(raw.start),
    end: StringDate.fromIsoLocalDateTime(raw.end),
    operatorUsername: raw.operatorUsername,
    operatorId: brandNumber(raw.operatorId),
  };
}

function mapDelayedClosureRequest(
  raw: SubscriptionView["delayedClosureRequests"][number],
): SubscriptionDelayedClosureRequest {
  return {
    id: brandNumber(raw.id),
    startExecutionDateTime: StringDate.fromIsoLocalDateTime(raw.startExecutionDateTime),
    scheduledClosureDateTime: StringDate.fromIsoLocalDateTime(raw.scheduledClosureDateTime),
    creationDatetime: StringDate.fromIsoLocalDateTime(raw.creationDatetime),
    origin: raw.origin,
    operatorUsername: raw.operatorUsername,
    operatorId: brandNumber(raw.operatorId),
    processingResult: raw.processingResult
      ? enumValueOrRaw(DelayedClosureProcessingResult, raw.processingResult)
      : undefined,
    failureReasonCode: brandString(raw.failureReasonCode),
    failureReasonLabel: raw.failureReasonLabel,
  };
}

function mapFailure(raw: NonNullable<SubscriptionView["failure"]>): SubscriptionFailure {
  return {
    // Raw codes are not useful in Voltacall
    reason: raw.reasonLabel,
    category: raw.categoryLabel,
    operatorUsername: raw.operatorUsername,
    operatorId: brandNumber(raw.operatorId),
    comment: raw.comment ?? undefined,
  };
}

export function mapSubscriptionPipelineFailures(raw: SubscriptionPipelineFailures): SubscriptionPipelineFailuresModel {
  return {
    pipelineId: brandNumber<SubscriptionPipelineId>(raw.pipelineId),
    referential: {
      PHONING_DELAYED_KO: raw.referential.PHONING_DELAYED_KO.map(mapFailureCategory),
      PHONING_IMMEDIATE_KO: raw.referential.PHONING_IMMEDIATE_KO.map(mapFailureCategory),
    },
  };
}

function mapFailureCategory(raw: FailureCategory): FailureCategoryModel {
  return {
    id: brandNumber<SubscriptionFailureCategoryId>(raw.id),
    name: raw.name,
    code: brandString(raw.code),
    comment: raw.comment ?? undefined,
    reasons: raw.reasons.map(mapFailureReason),
  };
}

function mapFailureReason(raw: FailureReason): FailureReasonModel {
  return {
    id: brandNumber<SubscriptionFailureReasonId>(raw.id),
    name: raw.name,
    code: brandString(raw.code),
    comment: raw.comment ?? undefined,
  };
}

export function mapSubscriptionDataTable(raw: SubscriptionDataTableResponse): SubscriptionDataTableResult {
  return {
    totalRows: raw.totalRows,
    invalidFilters: raw.invalidFilters.map(mapInvalidFilter),
    rows: (raw.rows ?? []).map((r) => mapDataTableRow(r)),
  };
}

function mapDataTableRow(raw: NonNullable<SubscriptionDataTableResponse["rows"]>[number]): SubscriptionDataTableRow {
  return {
    id: brandNumber<SubscriptionId>(raw.id),
    subscriberFirstName: raw.subscriberFirstName,
    subscriberLastName: raw.subscriberLastName,
    status: enumValueOrRaw(SubscriptionStatus, raw.status),
    pipelineId: brandNumber<SubscriptionPipelineId>(raw.pipelineId),
    creationDatetime: StringDate.fromIsoLocalDateTime(raw.creationDatetime),
    closureDatetime: raw.closureDateTime ? StringDate.fromIsoLocalDateTime(raw.closureDateTime) : undefined,
    failure: raw.subscriptionFailure ? mapHistoryFailure(raw.subscriptionFailure) : undefined,
  };
}

function mapHistoryFailure(raw: SubscriptionDataTableFailure): SubscriptionHistoryFailure {
  return {
    reasonCode: brandString(raw.reasonCode),
    reason: raw.reasonLabel,
    comment: raw.comment ?? undefined,
    declaredBy: raw.personDeclaringFailure ? mapHistoryFailureAuthor(raw.personDeclaringFailure) : undefined,
  };
}

function mapHistoryFailureAuthor(raw: SubscriptionDataTablePersonDeclaringFailure): SubscriptionHistoryFailureAuthor {
  return {
    id: brandNumber(raw.id),
    firstName: raw.firstname,
    lastName: raw.lastname,
    login: raw.login,
  };
}

function mapInvalidFilter(
  raw: SubscriptionDataTableResponse["invalidFilters"][number],
): SubscriptionDataTableResult["invalidFilters"][number] {
  return {
    name: raw.name,
    code: raw.code,
    message: raw.message ?? undefined,
  };
}
